import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  FigureElement,
  FigureHandle,
  FormulaLegendHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import { createCartesianViewport, createFormulaLegend } from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { compileFn, sampleFn, forwardDifference, symmetricDifference } from '../calc';
import { TANGENT_PRESETS } from '../data/tangent-presets';
import { parse, derivative, simplify, stringify, evaluate } from '@learnforge/demo-math/expr';
import { createCartesianPlane, type CartesianPlaneHandle } from '../render/cartesian-plane';

// ─── Stage layout constants ───────────────────────────────────────────────────

/** Stage width and height in pixels. */
const STAGE_W = 800;
const STAGE_H = 460;
const BASE_Y_DOMAIN: [number, number] = [-5.75, 5.75];
const PLOT_RECT = { x: 0, y: 0, width: STAGE_W, height: STAGE_H };
/** Number of sample points for the function curve. */
const SAMPLE_N = 400;
/** Clamp limit for a/h values. */
const A_HALF_RANGE = 5;
const H_MIN = 0.05;
const H_MAX = 4;

// ─── State ────────────────────────────────────────────────────────────────────

export interface TangentLabState {
  presetId: string;
  /** Base point x-coordinate in math space. */
  a: number;
  /** Step size: Q is at (a+h, f(a+h)). */
  h: number;
  /** Which difference quotient formula to use. */
  mode: 'forward' | 'symmetric';
}

// ─── Exported pure helpers (unit-tested) ─────────────────────────────────────

/**
 * Return the symbolic source string for f′(x) given the source of f.
 * Uses the engine: parse → differentiate → simplify → stringify.
 */
export function exactDerivativeSource(source: string): string {
  return stringify(simplify(derivative(parse(source).node, 'x')));
}

/**
 * Compile the preset's source function and compute the difference quotient
 * at the current (a, h) according to the chosen mode.
 */
export function secantSlope(state: TangentLabState): number {
  const preset = TANGENT_PRESETS.find((p) => p.id === state.presetId) ?? TANGENT_PRESETS[0]!;
  const f = compileFn(preset.source);
  if (state.mode === 'symmetric') {
    return symmetricDifference(f, state.a, state.h);
  }
  return forwardDifference(f, state.a, state.h);
}

/**
 * Build the readout rows for the tangent-lab panel.
 *
 * Rows:
 *  - `quotient`  : [f(a+h)−f(a)]/h = <numeric>    (or symmetric variant)
 *  - `derivative`: → f′(a) = <numeric>
 *  - `exact`     : f′(x) = <symbolicSource>
 */
export function tangentReadoutModel(state: TangentLabState): ReadoutRow[] {
  const preset = TANGENT_PRESETS.find((p) => p.id === state.presetId) ?? TANGENT_PRESETS[0]!;
  const derivSrc = exactDerivativeSource(preset.source);
  const derivNode = parse(derivSrc).node;

  const slope = secantSlope(state);
  const fPrimeA = evaluate(derivNode, { x: state.a });

  const lhsQuotient = state.mode === 'forward' ? '[f(a+h)−f(a)] / h' : '[f(a+h)−f(a−h)] / 2h';

  return [
    {
      id: 'quotient',
      lhs: lhsQuotient,
      relation: '=' as const,
      rhs: formatNumber(slope, { decimals: 4 }),
      emphasis: 'neutral' as const,
    },
    {
      id: 'derivative',
      lhs: '→ f′(a)',
      relation: '=' as const,
      rhs: formatNumber(fPrimeA, { decimals: 4 }),
      emphasis: 'invariant' as const,
    },
    {
      id: 'exact',
      lhs: 'f′(x)',
      relation: '=' as const,
      rhs: derivSrc,
      emphasis: 'neutral' as const,
    },
  ];
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<TangentLabState, CalcKey, CalculusContext> {
  // Closure-stashed handles created in setup():
  let plane: CartesianPlaneHandle;
  let figure: FigureHandle;
  let curves: CurveLayerHandle;
  let readout: ReadoutHandle;
  let viewport: CartesianViewportHandle;
  let legend: FormulaLegendHandle;
  let activePresetId = TANGENT_PRESETS[0]!.id;

  function baseDomains(preset: (typeof TANGENT_PRESETS)[number]) {
    return { xDomain: preset.domain, yDomain: BASE_Y_DOMAIN };
  }

  function defaultState(_ctx: CalculusContext): TangentLabState {
    const preset = TANGENT_PRESETS[0]!;
    return {
      presetId: preset.id,
      a: preset.a,
      h: 1.0,
      mode: 'forward',
    };
  }

  function applyPresetLocal(state: TangentLabState, id: string): TangentLabState {
    const preset = TANGENT_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    return { ...state, presetId: preset.id, a: preset.a, h: 1.0 };
  }

  function getPreset(state: TangentLabState) {
    return TANGENT_PRESETS.find((p) => p.id === state.presetId) ?? TANGENT_PRESETS[0]!;
  }

  /** Evaluate f at x, returning NaN on error. */
  function evalF(source: string, x: number): number {
    try {
      return compileFn(source)(x);
    } catch {
      return NaN;
    }
  }

  return {
    labelKey: 'experiment.tangent-lab',
    demoId: 'calculus',
    advancedKey: 'tangent-lab',

    initialState(ctx): TangentLabState {
      return defaultState(ctx);
    },

    applyPreset(state, id): TangentLabState {
      return applyPresetLocal(state, id);
    },

    setup(ctx, controller: HarnessController<TangentLabState>): void {
      const base = baseDomains(TANGENT_PRESETS[0]!);
      plane = createCartesianPlane(controller.svg, { domains: base, plotRect: PLOT_RECT });
      controller.registerCleanup(() => plane.destroy());
      curves = createCurveLayer(plane.content, plane);
      controller.registerCleanup(() => curves.destroy());
      figure = controller.createFigure();
      readout = controller.createReadout();
      legend = createFormulaLegend(ctx.root);
      controller.registerCleanup(() => legend.destroy());
      viewport = createCartesianViewport(controller.svg, {
        base,
        plotRect: PLOT_RECT,
        onChange(next) {
          plane.setDomains(next);
          controller.requestRender();
        },
      });
      controller.registerCleanup(() => viewport.dispose());
    },

    render(state: TangentLabState, ctx): void {
      const preset = getPreset(state);
      if (preset.id !== activePresetId) {
        const base = baseDomains(preset);
        activePresetId = preset.id;
        viewport.setBase(base, { notify: false });
        plane.setDomains(base);
      }
      const { a, h } = state;
      const source = preset.source;
      const f = compileFn(source);

      // ── Derivative for tangent slope ──
      const derivSrc = exactDerivativeSource(source);
      const derivNode = parse(derivSrc).node;
      const fPrimeA = (() => {
        try {
          return evaluate(derivNode, { x: a });
        } catch {
          return NaN;
        }
      })();

      // ── Function values at key points ──
      const fa = evalF(source, a);
      const fah = evalF(source, a + h);

      // ── Function curve (sampled across visible domain) ──
      const { xDomain } = plane.getDomains();
      const [xMin, xMax] = xDomain;
      curves.setPolyline('f', sampleFn(f, xMin, xMax, SAMPLE_N), 'calc-curve-primary');

      // ── Secant line: extend through P and Q across the full view ──
      // Draw as a long segment rather than the infinite line kind, to keep
      // it clearly visible without going off-screen.
      if (Number.isFinite(fa) && Number.isFinite(fah) && Math.abs(h) > 1e-9) {
        const secantM = (fah - fa) / h;
        const sx0 = xMin;
        const sy0 = fa + secantM * (sx0 - a);
        const sx1 = xMax;
        const sy1 = fa + secantM * (sx1 - a);
        curves.setPolyline(
          'secant',
          [
            { x: sx0, y: sy0 },
            { x: sx1, y: sy1 },
          ],
          'calc-curve-secondary',
        );
      } else {
        curves.clear('secant');
      }

      // ── Tangent line: through P with slope f′(a) ──
      if (Number.isFinite(fa) && Number.isFinite(fPrimeA)) {
        const tx0 = xMin;
        const ty0 = fa + fPrimeA * (tx0 - a);
        const tx1 = xMax;
        const ty1 = fa + fPrimeA * (tx1 - a);
        curves.setPolyline(
          'tangent',
          [
            { x: tx0, y: ty0 },
            { x: tx1, y: ty1 },
          ],
          'calc-curve-tangent',
        );
      } else {
        curves.clear('tangent');
      }

      // ── Figure elements ──
      const ptP = { x: a, y: fa };
      const ptQ = { x: a + h, y: fah };
      // Hidden helper point T on the tangent line, 1 world unit to the right
      const ptT = { x: a + 1, y: fa + fPrimeA };

      const screenP = plane.worldToScreen(ptP);
      const screenQ = plane.worldToScreen(ptQ);
      const screenT = plane.worldToScreen(ptT);

      const elements: FigureElement[] = [
        // P — draggable horizontally, moves 'a'
        {
          kind: 'point',
          id: 'pt-P',
          at: screenP,
          style: { className: 'geo-point-draggable' },
          draggable: {},
        },
        // Q — draggable, moving sets h
        {
          kind: 'point',
          id: 'pt-Q',
          at: screenQ,
          style: { className: 'geo-point-secondary geo-point-draggable' },
          draggable: {},
        },
        // Hidden helper T for tangent direction (not draggable)
        {
          kind: 'point',
          id: 'pt-T',
          at: screenT,
          style: { className: 'geo-point-off-stage' },
        },
      ];

      // Advanced: add error row showing forward vs symmetric difference
      if (ctx.advanced) {
        // Draw a small visual indicator — a dot on the curve at the symmetric midpoint
        const midX = a + (state.mode === 'symmetric' ? 0 : h / 2);
        const midY = evalF(source, midX);
        if (Number.isFinite(midY)) {
          elements.push({
            kind: 'point',
            id: 'pt-mid',
            at: plane.worldToScreen({ x: midX, y: midY }),
            style: { className: 'geo-point-main' },
          });
        }
      }

      figure.update(elements);
      for (const id of ['pt-P', 'pt-Q']) {
        curves.group.ownerSVGElement
          ?.querySelector(`[data-figure-id="${id}"]`)
          ?.setAttribute('data-no-pan', 'true');
      }

      const fmt = (value: number) =>
        formatNumber(value, { decimals: 3 }).replace(/(?:\.0+|(\.\d*?)0+)$/, '$1');
      const slopeSign = fPrimeA < 0 ? '−' : '+';
      const centerSign = a < 0 ? '+' : '−';
      legend.setEntries([
        { id: 'f', formula: `f(x) = ${source}`, color: 'var(--accent)' },
        {
          id: 'tangent',
          formula: `y = ${fmt(fa)} ${slopeSign} ${fmt(Math.abs(fPrimeA))}(x ${centerSign} ${fmt(Math.abs(a))})`,
          color: 'var(--red)',
          lineStyle: 'dashed',
        },
      ]);

      // ── Readout ──
      const rows = tangentReadoutModel(state);
      if (ctx.advanced) {
        // Add an error row showing forward − symmetric
        const fwdSlope = forwardDifference(f, a, h);
        const symSlope = symmetricDifference(f, a, h);
        const err = Math.abs(fwdSlope - symSlope);
        rows.push({
          id: 'error',
          lhs: '|fwd − sym|',
          relation: '=' as const,
          rhs: err < 1e-10 ? '< 1e−10' : err.toExponential(3),
          emphasis: 'neutral' as const,
        });
      }
      readout.update(rows);
    },

    onDrag(state: TangentLabState, pointId: string, at: Vec2): TangentLabState {
      const mathX = plane.screenToWorld(at).x;

      if (pointId === 'pt-P') {
        // Dragging P moves 'a'; clamp to the curated preset range.
        const newA = Math.max(
          getPreset(state).domain[0] + A_HALF_RANGE * 0.1,
          Math.min(getPreset(state).domain[1] - A_HALF_RANGE * 0.1, mathX),
        );
        return { ...state, a: newA };
      }

      if (pointId === 'pt-Q') {
        // Dragging Q sets h from its Cartesian x-coordinate relative to a.
        const newH = mathX - state.a;
        // Clamp h to [H_MIN, H_MAX] or [−H_MAX, −H_MIN]
        const clampedH =
          newH >= 0
            ? Math.max(H_MIN, Math.min(H_MAX, newH))
            : Math.min(-H_MIN, Math.max(-H_MAX, newH));
        return { ...state, h: clampedH };
      }

      return state;
    },

    panel(state: TangentLabState, ctx): PanelRow<CalcKey, TangentLabState>[] {
      const rows: PanelRow<CalcKey, TangentLabState>[] = [];

      // Preset dropdown
      rows.push({
        kind: 'dropdown',
        id: 'tangent-preset',
        labelKey: 'tangent.panel.preset',
        options: TANGENT_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      // 'a' slider
      rows.push({
        kind: 'slider',
        id: 'tangent-a',
        labelKey: 'tangent.panel.a',
        min: -4,
        max: 4,
        step: 0.05,
        value: state.a,
        format: (v) => v.toFixed(2),
        onChange: (v) => ({ ...state, a: v }),
      });

      // 'h' slider
      rows.push({
        kind: 'slider',
        id: 'tangent-h',
        labelKey: 'tangent.panel.h',
        min: 0.05,
        max: 4,
        step: 0.05,
        value: state.h,
        format: (v) => v.toFixed(2),
        onChange: (v) => ({ ...state, h: v }),
      });

      // Advanced toggle (must use 'panel.advanced' as the label for the toggle row)
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      // Advanced: mode selector (forward / symmetric) — uses its own label key
      if (ctx.advanced) {
        rows.push({
          kind: 'dropdown',
          id: 'tangent-mode',
          labelKey: 'tangent.panel.mode',
          options: [
            { value: 'forward', labelKey: 'tangent.mode.forward' },
            { value: 'symmetric', labelKey: 'tangent.mode.symmetric' },
          ],
          value: state.mode,
          onChange: (v) => ({ ...state, mode: v as 'forward' | 'symmetric' }),
        });
      }

      return rows;
    },

    actions: (_state: TangentLabState, ctx): ActionBarSpec<CalcKey, TangentLabState> => {
      return {
        onReset: () => defaultState(ctx),
        onRandomise: (s) => {
          const idx = Math.floor(ctx.prng() * TANGENT_PRESETS.length);
          const preset = TANGENT_PRESETS[idx]!;
          return { ...s, presetId: preset.id, a: preset.a, h: 1.0 };
        },
        presets: TANGENT_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport.reset() },
      };
    },

    applyShotState(state: TangentLabState, _shotId: string): TangentLabState {
      // Pin h to a clearly visible gap for screenshots
      return { ...state, h: 0.8 };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
